/*
 * HDS header panel toggle — generic open/close for any panel anchored under
 * the floating pill (search, account, etc.).
 *
 * Markup contract:
 *   <button data-hds-panel-toggle="<name>" aria-expanded="false" aria-controls="<id>">
 *   <div   data-hds-panel="<name>" id="<id>" hidden> ... </div>
 *
 * Optional inside the panel:
 *   [data-hds-panel-focus]  — element to focus when the panel opens
 *   [data-hds-panel-close]  — element that closes the panel when clicked
 *
 * Special: the search panel dispatches `dialog:close:search` on close so the
 * Shopify <predictive-search> component tears down its results cleanly.
 */
(function () {
  const panels = document.querySelectorAll('[data-hds-panel]');
  if (!panels.length) return;

  const registry = new Map();
  let activeName = null;

  panels.forEach((panel) => {
    const name = panel.dataset.hdsPanel;
    if (!name) return;
    // querySelectorAll so a panel can have multiple toggles (e.g. the search
    // panel is opened by both the desktop pill icon and the mobile drawer link).
    const toggles = Array.from(
      document.querySelectorAll(`[data-hds-panel-toggle="${name}"]`)
    );
    const closeBtn = panel.querySelector('[data-hds-panel-close]');
    const focusEl =
      panel.querySelector('[data-hds-panel-focus]') ||
      panel.querySelector('input, button, [tabindex]');
    registry.set(name, { panel, toggles, closeBtn, focusEl });
  });

  // Megamenu (hover) and panels (click) must never show together.
  function closeOpenMegamenus() {
    const headerMenu = document.querySelector('header-menu');
    if (!headerMenu || typeof headerMenu.closeSubmenu !== 'function') return;
    headerMenu.querySelectorAll('[aria-expanded="true"]').forEach((trigger) => {
      const submenu = document.getElementById(trigger.getAttribute('aria-controls'));
      if (submenu && submenu.getAttribute('data-submenu')) {
        headerMenu.closeSubmenu(submenu, true);
      }
    });
  }

  function open(name) {
    closeAll(name);
    closeOpenMegamenus();
    const entry = registry.get(name);
    if (!entry || !entry.panel.hidden) return;
    // Lock pill before hidden=false (which triggers body{overflow:hidden}).
    if (name === 'menu') lockPillForMenu();
    entry.panel.hidden = false;
    entry.toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    // preventScroll: the input lives inside a sticky header that's already in
    // view, but the browser doesn't know that and would scroll the page up.
    requestAnimationFrame(() => entry.focusEl?.focus({ preventScroll: true }));
    activeName = name;
    document.addEventListener('keydown', onEsc);
    document.addEventListener('click', onOutside, true);
  }

  function close(name) {
    const entry = registry.get(name);
    if (!entry || entry.panel.hidden) return;
    entry.panel.hidden = true;
    entry.toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    // Unlock pill AFTER hidden=true (sticky needs body scroll restored first).
    if (name === 'menu') unlockPillForMenu();
    if (name === 'search') {
      document.dispatchEvent(new CustomEvent('dialog:close:search'));
    }
    if (activeName === name) {
      activeName = null;
      document.removeEventListener('keydown', onEsc);
      document.removeEventListener('click', onOutside, true);
    }
  }

  function closeAll(except) {
    registry.forEach((_entry, name) => {
      if (name !== except) close(name);
    });
  }

  function onEsc(e) {
    if (e.key === 'Escape' && activeName) close(activeName);
  }

  function onOutside(e) {
    if (!activeName) return;
    // A dialog opened on top (e.g. localization) handles its own outside-click.
    if (e.target.closest('dialog[open]')) return;
    const entry = registry.get(activeName);
    if (entry.panel.contains(e.target)) return;
    if (entry.toggles.some((t) => t.contains(e.target))) return;
    close(activeName);
  }

  // Pin pill at its current viewport rect — sticky breaks under body{overflow:hidden}.
  function lockPillForMenu() {
    const pill = document.querySelector('.hds-header__pill');
    if (!pill) return;
    const r = pill.getBoundingClientRect();
    pill.style.position = 'fixed';
    pill.style.top = `${r.top}px`;
    pill.style.left = `${r.left}px`;
    pill.style.width = `${r.width}px`;
    pill.style.margin = '0';
    pill.style.maxWidth = 'none';
    const root = document.documentElement.style;
    // -1px: hide subpixel seam.
    root.setProperty('--hds-menu-top', `${r.bottom - 1}px`);
    root.setProperty('--hds-menu-left', `${r.left}px`);
    root.setProperty('--hds-menu-right', `${window.innerWidth - r.right}px`);
  }

  function unlockPillForMenu() {
    const pill = document.querySelector('.hds-header__pill');
    if (!pill) return;
    pill.style.position = '';
    pill.style.top = '';
    pill.style.left = '';
    pill.style.width = '';
    pill.style.margin = '';
    pill.style.maxWidth = '';
    const root = document.documentElement.style;
    root.removeProperty('--hds-menu-top');
    root.removeProperty('--hds-menu-left');
    root.removeProperty('--hds-menu-right');
  }

  registry.forEach(({ toggles, panel, closeBtn }, name) => {
    toggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        // Stop Primavera's header-menu.js global click handler from scanning
        // our [aria-expanded][aria-controls] toggle as a submenu trigger and
        // resetting aria-expanded behind our back.
        e.stopPropagation();
        if (panel.hidden) open(name);
        else close(name);
      });
    });
    closeBtn?.addEventListener('click', () => {
      setTimeout(() => close(name), 0);
    });
  });

  document.addEventListener('hds:megamenu:opened', () => closeAll());
  // Opening the search modal (incl. the mobile menu's Search button) closes any open panel.
  document.addEventListener('dialog:open:search', () => closeAll());
})();
